/**
 * 🔍 Tenant Resolver - Detecta el tenant desde el dominio/subdominio
 *
 * ARQUITECTURA (estilo Shopify):
 * - smarty.lat / www.smarty.lat → Marketing (retorna null)
 * - admin.smarty.lat → Panel admin (retorna null, tenant desde sesión)
 * - dulti.smarty.lat → Ecommerce del tenant "dulti" (público)
 */

import { getTenantSubdomain } from '@/lib/domain';
import { devLog } from '@/lib/utils';
import { ROOT_DOMAIN_PROD, ROOT_DOMAIN_DEV } from '@/config/app-domains.config';

/**
 * Detecta el subdominio/tenant desde el hostname actual (solo ecommerce).
 * smarty.lat → null; admin.smarty.lat → null; dulti.smarty.lat → "dulti"
 */
export function getTenantFromDomain(): string | null {
  return getTenantSubdomain();
}

async function getTenantIdFromSubdomain(subdomain: string): Promise<string | null> {
  if (!subdomain) return null;

  try {
    const { supabase } = await import('@/lib/supabase');

    const { data: tenantDomain, error: domainError } = await supabase
      .from('tenant_domains')
      .select('tenant_id')
      .eq('subdomain', subdomain)
      .eq('is_active', true)
      .single();

    if (!domainError && tenantDomain?.tenant_id) {
      return tenantDomain.tenant_id;
    }

    if (import.meta.env.DEV) {
      const defaultTenantId = import.meta.env.VITE_PUBLIC_TENANT_ID as string | undefined;
      if (defaultTenantId && subdomain === 'dulti') {
        devLog.warn('🔧 [tenant-resolver] Modo desarrollo: usando tenant por defecto para subdomain:', subdomain);
        return defaultTenantId;
      }
    }

    return null;
  } catch (error) {
    if (import.meta.env.DEV) {
      const defaultTenantId = import.meta.env.VITE_PUBLIC_TENANT_ID as string | undefined;
      if (defaultTenantId && subdomain === 'dulti') return defaultTenantId;
    }
    devLog.error('[tenant-resolver] Error obteniendo tenant_id:', error);
    return null;
  }
}

async function getTenantIdFromCustomDomain(domain: string): Promise<string | null> {
  if (!domain) return null;

  const normalizedDomain = domain.toLowerCase().replace(/^www\./, '');

  try {
    const { supabase } = await import('@/lib/supabase');

    const { data: tenantDomain, error } = await supabase
      .from('tenant_domains')
      .select('tenant_id')
      .eq('domain', normalizedDomain)
      .eq('is_custom', true)
      .eq('is_active', true)
      .eq('is_verified', true)
      .single();

    if (error) {
      if (error.code === 'PGRST116' || error.code === '42P01') return null;
      if (import.meta.env.DEV) {
        devLog.warn('[tenant-resolver] Dominio personalizado no encontrado:', normalizedDomain);
      }
      return null;
    }

    if (!tenantDomain?.tenant_id) return null;
    return tenantDomain.tenant_id;
  } catch (error) {
    if (import.meta.env.DEV) {
      devLog.warn('[tenant-resolver] Error obteniendo tenant desde dominio personalizado:', error);
    }
    return null;
  }
}

/**
 * Obtiene el tenant_id desde el dominio actual (solo para ecommerce).
 * dulti.smarty.lat → tenant_id de "dulti"; smarty.lat / admin.smarty.lat → null.
 */
export async function resolveTenantId(): Promise<string | null> {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

  // Dominios reservados: marketing, panel web (mercaderes) y super admin
  const reserved =
    hostname === ROOT_DOMAIN_PROD ||
    hostname === `www.${ROOT_DOMAIN_PROD}` ||
    hostname === ROOT_DOMAIN_DEV ||
    hostname === `www.${ROOT_DOMAIN_DEV}` ||
    hostname === 'web.smarty.lat' ||
    hostname === 'web.smarty.local' ||
    hostname === 'admin.smarty.lat' ||
    hostname === 'admin.smarty.local';
  if (reserved) return null;

  const normalizedHostname = hostname.replace(/^www\./, '');

  // Dominio personalizado (no es *.smarty.lat ni *.smarty.local)
  if (!normalizedHostname.endsWith(`.${ROOT_DOMAIN_PROD}`) && !normalizedHostname.endsWith(`.${ROOT_DOMAIN_DEV}`)) {
    const customTenantId = await getTenantIdFromCustomDomain(normalizedHostname);
    if (customTenantId) return customTenantId;
    return null;
  }

  const subdomain = getTenantFromDomain();
  if (!subdomain) return null;

  return await getTenantIdFromSubdomain(subdomain);
}
